
import { useState, useEffect } from "react";
import { getPurchaseById, refreshPurchaseStatus, Purchase } from "@/lib/purchases";
import { toast } from "@/components/ui/use-toast";

export function usePurchaseStatus(purchaseId: string | null) {
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [loading, setLoading] = useState(!!purchaseId);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefreshActive, setAutoRefreshActive] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);
  const [refreshTimedOut, setRefreshTimedOut] = useState(false);
  
  // Maximum number of auto-refresh attempts (12 x 5s = 60 seconds)
  const MAX_REFRESH_COUNT = 12;

  // Check for status parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    
    // If URL contains status=cancelled, override the purchase status
    if (status === 'cancelled' && purchase) {
      setPurchase({
        ...purchase,
        payment_status: 'cancelled',
        status: 'cancelled'
      });
      setAutoRefreshActive(false);
    }
  }, [purchase]);

  // Fetch purchase/payment status on mount
  useEffect(() => {
    const fetchPurchase = async () => {
      if (!purchaseId) return;
      setLoading(true);

      try {
        const p = await getPurchaseById(purchaseId);
        
        // Check if URL has status=cancelled and override if needed
        const urlParams = new URLSearchParams(window.location.search);
        const status = urlParams.get('status');
        
        if (status === 'cancelled' && p) {
          setPurchase({
            ...p,
            payment_status: 'cancelled',
            status: 'cancelled'
          });
          setAutoRefreshActive(false);
        } else {
          setPurchase(p);
          
          // If payment is pending, start auto-refresh
          if (p && (p.payment_status === 'pending' || p.payment_status === 'processing')) {
            setAutoRefreshActive(true);
          } else {
            setAutoRefreshActive(false);
          }
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load purchase details",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchPurchase();
  }, [purchaseId]);

  // If payment is in pending/processing state, auto-refresh every 5 seconds with timeout
  useEffect(() => {
    if (!autoRefreshActive || !purchaseId) return;
    
    const intervalId = setInterval(async () => {
      try {
        // Increment refresh count
        setRefreshCount(prev => {
          const newCount = prev + 1;
          
          // Check if we've reached the maximum refresh count
          if (newCount >= MAX_REFRESH_COUNT) {
            setAutoRefreshActive(false);
            setRefreshTimedOut(true);
            
            // If we still have a pending status after max retries, show timeout message
            if (purchase?.payment_status === 'pending' || purchase?.payment_status === 'processing') {
              toast({
                title: "Refresh Timeout",
                description: "Payment status check timed out. Please refresh manually.",
                variant: "default"
              });
              
              // Set purchase with timeout status
              setPurchase(prevPurchase => 
                prevPurchase ? {...prevPurchase, payment_status: 'timeout'} : null
              );
            }
          }
          
          return newCount;
        });
        
        // Skip the refresh if we've already timed out
        if (refreshTimedOut) return;
        
        const updatedPurchase = await refreshPurchaseStatus(purchaseId);
        
        if (updatedPurchase) {
          setPurchase(updatedPurchase);
          
          // If payment is no longer pending, stop auto-refresh
          if (
            updatedPurchase.payment_status === 'completed' || 
            updatedPurchase.status === 'completed' ||
            updatedPurchase.payment_status === 'failed' || 
            updatedPurchase.payment_status === 'cancelled' ||
            updatedPurchase.status === 'failed' ||
            updatedPurchase.status === 'cancelled'
          ) {
            setAutoRefreshActive(false);
          }
        }
      } catch (error) {
        setAutoRefreshActive(false);
      }
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(intervalId);
  }, [autoRefreshActive, purchaseId, purchase, refreshTimedOut]);

  // Handler to manually refresh the payment status
  const handleRefreshStatus = async () => {
    if (!purchaseId) return;
    
    setRefreshing(true);
    toast({
      title: "Värskendamine...",
      description: "Kontrollime makse staatust...",
    });
    
    try {
      const updatedPurchase = await refreshPurchaseStatus(purchaseId);
      
      if (updatedPurchase) {
        setPurchase(updatedPurchase);
        
        if (updatedPurchase.payment_status === 'completed' || 
            updatedPurchase.status === 'completed') {
          toast({
            title: "Värskendatud!",
            description: "Makse on edukalt lõpetatud!",
            variant: "default",
          });
        } else if (updatedPurchase.payment_status === 'failed' || 
                  updatedPurchase.payment_status === 'cancelled' ||
                  updatedPurchase.status === 'failed' ||
                  updatedPurchase.status === 'cancelled') {
          toast({
            title: "Värskendatud!",
            description: updatedPurchase.payment_status === 'cancelled' 
              ? "Makse tühistati."
              : "Makse ebaõnnestus.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Värskendatud!",
            description: "Makse on endiselt töötlemisel. Palun proovi hiljem uuesti.",
          });
        }
      } else {
        toast({
          title: "Värskendamine ebaõnnestus",
          description: "Ei saanud värskendada makse staatust. Palun proovi hiljem uuesti.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Värskendamine ebaõnnestus",
        description: "Tekkis viga makse staatuse värskendamisel.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
      // Reset the refresh count when manually refreshing
      setRefreshCount(0);
      setRefreshTimedOut(false);
    }
  };

  return {
    purchase,
    loading,
    refreshing,
    autoRefreshActive,
    refreshTimedOut,
    handleRefreshStatus
  };
}
